import { useEffect, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { convertStringToErrorCode, ErrorCode } from '../api/errorCode';
import { useAuthTokenStore } from '../store/authStore';
import { useGlobalErrStore } from '../store/errorStore';
import { usePageOwnerIdentityStore } from '../store/pageOwnerIdentityStore';
import { isPageOwnerMatchedToRaw } from '../util/pageOwnerMatch';
import { runCurrentArchiveRequest } from '../util/currentArchiveRequest';
import { useAuth } from './useApi/useAuth';
import { hasUnsavedChanges } from './useCheckBeforeMove';

const ACCESS_DENIED_CODES = new Set<string>([
  ErrorCode.NFR,
  ErrorCode.UAA,
  ErrorCode.NAU,
]);

const idFromHash = (hash: string): string | null => {
  const raw = hash.startsWith('#') ? hash.slice(1) : hash;
  if (!raw) return null;
  try {
    const id = decodeURIComponent(raw).trim();
    return id || null;
  } catch {
    const id = raw.trim();
    return id || null;
  }
};

const hashForId = (id: string) => `#${encodeURIComponent(id)}`;

const splitApiError = (error: unknown): { code: string; message: string } | null => {
  if (!(error instanceof Error)) return null;
  const separator = error.message.indexOf('::');
  if (separator < 0) return null;
  return {
    code: error.message.slice(0, separator).trim(),
    message: error.message.slice(separator + 2).trim(),
  };
};

interface OpenFromHashOptions<T> {
  activeId: string | null;
  getActiveId: () => string | null;
  request: (aui: string, id: string, isOwner: boolean) => Promise<{ data: T }>;
  isInaccessible: (data: T, isOwner: boolean) => boolean;
  open: (data: T) => void;
}

/**
 * `#id` 로 현재 아카이브의 항목을 연다.
 * 권한이 없거나 없는 id면 항목은 열지 않고 #만 지운다.
 * 목록에서 연 항목은 같은 #으로 주소에 반영한다.
 * URL에 #이 없으면 스토어에 남은 선택으로 #을 만들지 않는다.
 */
export const useOpenFromHash = <T,>({
  activeId,
  getActiveId,
  request,
  isInaccessible,
  open,
}: OpenFromHashOptions<T>) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { HANDLE: raw } = useParams<{ HANDLE?: string }>();
  const pageOwner = usePageOwnerIdentityStore((s) => s.pageOwner);
  const { user, isOwner } = useAuth();
  const authToken = useAuthTokenStore((s) => s.authToken);

  const requestGenRef = useRef(0);
  const pendingHashRef = useRef<string | null>(null);
  const prevHashRef = useRef<string | null>(null);
  const prevActiveRef = useRef<string | null | undefined>(undefined);
  const archiveRef = useRef<string | null>(null);
  const ownerModeRef = useRef<boolean | null>(null);
  const getActiveIdRef = useRef(getActiveId);
  const requestFnRef = useRef(request);
  const isInaccessibleRef = useRef(isInaccessible);
  const openRef = useRef(open);
  getActiveIdRef.current = getActiveId;
  requestFnRef.current = request;
  isInaccessibleRef.current = isInaccessible;
  openRef.current = open;

  const authReady = !(user && !authToken);
  const pageOwnerAui = pageOwner?.aui;
  const ownerReady = isPageOwnerMatchedToRaw(raw, pageOwner);

  useEffect(() => {
    if (!authReady || !ownerReady || !pageOwnerAui) return;

    if (archiveRef.current !== pageOwnerAui) {
      archiveRef.current = pageOwnerAui;
      prevHashRef.current = null;
      prevActiveRef.current = undefined;
      pendingHashRef.current = null;
    }

    const hashId = idFromHash(location.hash);
    if (ownerModeRef.current !== isOwner) {
      ownerModeRef.current = isOwner;
      if (hashId && hashId !== activeId) {
        prevHashRef.current = null;
      }
    }

    const hashChanged = prevHashRef.current !== location.hash;
    const activeChanged = prevActiveRef.current !== activeId;
    prevHashRef.current = location.hash;
    prevActiveRef.current = activeId;

    const pathWithoutHash = `${location.pathname}${location.search}`;
    const writeHash = (id: string) => {
      const nextHash = hashForId(id);
      if (idFromHash(location.hash) === id) return;
      prevHashRef.current = nextHash;
      navigate(`${location.pathname}${location.search}${nextHash}`, { replace: true });
    };
    const stripHash = () => {
      pendingHashRef.current = null;
      if (!location.hash) return;
      prevHashRef.current = '';
      navigate(pathWithoutHash, { replace: true });
    };

    if (hashChanged && hashId && hashId !== activeId && pendingHashRef.current !== hashId) {
      if (hasUnsavedChanges(location.pathname)) {
        if (activeId) writeHash(activeId);
        else stripHash();
        return;
      }

      pendingHashRef.current = hashId;
      const requestId = ++requestGenRef.current;
      let cancelled = false;

      void (async () => {
        const finishPending = () => {
          if (pendingHashRef.current === hashId) pendingHashRef.current = null;
        };
        try {
          const response = await runCurrentArchiveRequest(
            pageOwnerAui,
            () => requestFnRef.current(pageOwnerAui, hashId, isOwner),
            (result) => result,
          );
          if (cancelled || requestId !== requestGenRef.current) return;
          if (!response) {
            finishPending();
            return;
          }

          const data = response.data;
          if (isInaccessibleRef.current(data, isOwner)) {
            stripHash();
            return;
          }

          if (cancelled || requestId !== requestGenRef.current) return;
          openRef.current(data);
          finishPending();
        } catch (error) {
          if (cancelled || requestId !== requestGenRef.current) return;
          finishPending();
          const parsed = splitApiError(error);
          if (parsed && ACCESS_DENIED_CODES.has(parsed.code)) {
            stripHash();
            return;
          }
          useGlobalErrStore.getState().setManagedErr({
            errCode: convertStringToErrorCode(parsed?.code ?? ErrorCode.WEF),
            value: parsed?.message,
          });
        }
      })();

      return () => {
        cancelled = true;
        requestGenRef.current += 1;
        if (pendingHashRef.current === hashId) pendingHashRef.current = null;
        const currentActive = getActiveIdRef.current();
        if (currentActive === hashId) return;
        prevHashRef.current = currentActive ? location.hash : null;
      };
    }

    if (pendingHashRef.current) return;

    if (activeChanged && !hashChanged) {
      if (activeId && hashId !== activeId) {
        writeHash(activeId);
        return;
      }
      if (!activeId && hashId) {
        stripHash();
      }
    }
  }, [
    authReady,
    ownerReady,
    pageOwnerAui,
    isOwner,
    activeId,
    location.hash,
    location.pathname,
    location.search,
    navigate,
  ]);
};
